import { Link } from "react-router-dom";
import { MonoLabel, Em, Card } from "@/site/components/Atoms";
import CrossBorderFlow from "@/site/anim/CrossBorderFlow";

const KOSOVO_POINTS = [
  [
    "01",
    "More Than a Tax Advantage",
    [
      "Tax positioning can be one part of Kosovo's relevance, but it is not the whole story. Forealco evaluates Kosovo through a broader lens: business substance, local coordination, banking and accounting readiness, digital potential, advisory support, institutional environment, commercial feasibility, and long-term operational credibility.",
    ],
  ],
  [
    "02",
    "Digital and Service-Based Business Models",
    [
      "Kosovo can be relevant for selected digital products, software-enabled services, platform models, advisory-supported service exports, finance-data-related business infrastructure, ERP-linked workflows, and operational tools designed for SMEs, accountants, financial institutions, and public-sector environments.",
    ],
  ],
  [
    "03",
    "Cross-Border Coordination Potential",
    [
      "Kosovo can support selected cross-border coordination models, including sales coordination, representation structures, trade operations, regional business development, service-based commercialization, and structured routes between Kosovo, Türkiye, the United Kingdom, the European Union, and the wider Western Balkans.",
      "Each route must be tested against legal, customs, political, logistical, financial, and commercial realities.",
    ],
  ],
  [
    "04",
    "Türkiye, the UK and Wider Trade Links",
    [
      "Kosovo's commercial relationships with Türkiye, the United Kingdom, the European Union, and regional frameworks create practical questions for businesses evaluating trade, services, structuring, sourcing, representation, or market-entry routes.",
      "Forealco examines these relationships not as abstract trade references, but as route-design questions: which entity should act, where contracts should sit, how invoices and documents should move, which rules of origin or customs issues apply, and how the structure can remain commercially credible.",
    ],
  ],
  [
    "05",
    "Institutional and Donor-Backed Opportunities",
    [
      "Kosovo's development environment includes public modernization needs, donor-backed programs, institutional transformation, digital government priorities, financial-sector modernization, infrastructure renewal, and selected procurement-related opportunities.",
      "Forealco helps assess where these opportunities are commercially realistic, structurally approachable, locally supportable, and suitable for the company's capability, references, and implementation model.",
    ],
  ],
  [
    "06",
    "Strategic Opportunity Areas",
    [
      "Forealco evaluates opportunities across selected strategic areas, including digital business infrastructure, public modernization, defense-related support and localization, energy transition, mining and natural resources, trade coordination, advisory-led market entry, startup commercialization, and financial-sector modernization.",
      "Each opportunity requires scoping, validation, partner alignment, and a clear operating route before it can become actionable.",
    ],
  ],
  [
    "07",
    "Future Markets",
    [
      "Forealco's model is not limited to Kosovo. The same approach can be applied to other markets where structured entry, selected local support, institutional understanding, trade logic, digital infrastructure, and business coordination can create practical value.",
      "Future markets are not treated as random expansion points. They are evaluated as connected business routes where Forealco's method can help test whether the market, partners, structure, and execution conditions are strong enough to proceed.",
    ],
  ],
];

const FOCUS_AREAS = [
  [
    "focus-digital",
    "01",
    "Digital Business Infrastructure",
    "Software, platforms, finance data, ERP-linked workflows, advisory-office tools, bank and accounting integrations, document movement, and digital service commercialization.",
  ],
  [
    "focus-trade",
    "02",
    "Trade & Cross-Border Coordination",
    "Structured trade routes, representation models, sales coordination, sourcing, rules-of-origin awareness, documentation control, and selected international business flows.",
  ],
  [
    "focus-public",
    "03",
    "Institutional & Public Modernization",
    "Digital government, donor-backed programs, modernization initiatives, citizen-facing services, registry systems, workflow redesign, and institutional transformation.",
  ],
  [
    "focus-defense",
    "04",
    "Defense-Related Support & Localization",
    "Support services, maintenance environments, facility operations, security technologies, surveillance, access control, localization, and institutional operating contexts.",
  ],
  [
    "focus-energy",
    "05",
    "Energy Transition & Infrastructure",
    "Selected opportunities around energy efficiency, public buildings, district heating, infrastructure coordination, engineering support, project monitoring, and local execution requirements.",
  ],
  [
    "focus-resources",
    "06",
    "Mining & Natural Resources",
    "Opportunity mapping, regulatory awareness, licensing sensitivity, partner evaluation, technical services, environmental considerations, and structured entry thinking.",
  ],
];

export default function Markets() {
  return (
    <>
      {/* HERO */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20">
        <div className="fade-up">
          <MonoLabel>MARKETS · CROSS-BORDER OPPORTUNITY FLOW</MonoLabel>
        </div>
        <h1 className="h-display fade-up delay-1 mt-6 text-[40px] sm:text-[54px] lg:text-[68px] max-w-5xl">
          Markets where opportunity, coordination, <br className="hidden md:block" />
          and infrastructure <Em>come together.</Em>
        </h1>
        <div className="fade-up delay-2 mt-7 max-w-2xl space-y-5 text-[15px] leading-[1.85] text-white/70">
          <p>
            Forealco does not treat markets as isolated jurisdictions. It evaluates how selected
            markets can become connected routes for entry, trade, digital commercialization,
            institutional opportunity, and operational execution.
          </p>
          <p>
            Kosovo is the first active opportunity landscape, while Türkiye, the United Kingdom,
            the European Union, and wider regional links form part of the route logic where
            relevant.
          </p>
        </div>

        <div className="mt-12">
          <div className="flex items-center justify-between mb-3">
            <MonoLabel>ORIGIN MARKETS</MonoLabel>
            <MonoLabel color="#E8ECF1">FOREALCO GATE</MonoLabel>
            <MonoLabel>ACTIVE LANDSCAPES</MonoLabel>
          </div>
          <CrossBorderFlow height={460} />
        </div>
      </section>

      {/* KOSOVO LANDSCAPE */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-28 grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <MonoLabel>FIRST ACTIVE OPPORTUNITY LANDSCAPE</MonoLabel>
            <h2 className="h-display mt-5 text-[34px] md:text-[42px] leading-[1.08]">
              Kosovo opportunity <Em>landscape.</Em>
            </h2>
            <div className="mt-7 max-w-md space-y-5 text-[14px] leading-[1.85] text-white/70">
              <p>
                Kosovo is Forealco's first active opportunity landscape. It offers a developing
                business environment where selected opportunities can be assessed across digital
                infrastructure, service commercialization, advisory-led entry, trade coordination,
                public modernization, institutional support, defense-related support, energy,
                natural resources, and cross-border business routes.
              </p>
              <p className="text-white/60">
                Kosovo is not presented as a shortcut or a generic low-tax destination. It is
                evaluated as a practical base where commercial purpose, local coordination, partner
                readiness, institutional context, and execution capacity must be tested together.
              </p>
            </div>
            <Link
              to="/explore-opportunities"
              data-testid="markets-kosovo-cta"
              className="btn-accent mt-8"
            >
              Explore Kosovo Opportunities →
            </Link>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 space-y-3">
          {KOSOVO_POINTS.map(([n, t, paragraphs]) => (
            <Card key={n} className="p-6 group">
              <div className="flex items-start gap-5">
                <span className="font-mono text-[10px] tracking-[0.3em] text-[#9DB4FF] mt-1.5">
                  {n}
                </span>
                <div className="flex-1">
                  <div className="font-display text-[22px] leading-[1.25]">{t}</div>
                  <div className="mt-3 space-y-3 text-[13px] leading-[1.8] text-white/70">
                    {paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* CONNECTED ROUTE */}
      <ConnectedRoute />

      {/* STRATEGIC OPPORTUNITY AREAS */}
      <section
        id="focus-areas"
        className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-28"
      >
        <div className="mb-12 max-w-3xl">
          <MonoLabel>STRATEGIC OPPORTUNITY AREAS</MonoLabel>
          <h2 className="h-display mt-4 text-[34px] md:text-[44px]">
            Where Forealco applies <Em>its model.</Em>
          </h2>
          <p className="mt-6 text-[14px] leading-[1.85] text-white/65">
            Forealco focuses on areas where market insight, local coordination, institutional
            understanding, partner readiness, and business infrastructure can create practical
            value. Each area is assessed through structure, feasibility, local fit, institutional
            context, and execution logic.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {FOCUS_AREAS.map(([id, n, t, d]) => (
            <Card key={id} id={id} className="p-6 group scroll-mt-24">
              <MonoLabel>{n}</MonoLabel>
              <div className="mt-3 font-display text-[22px] leading-[1.25]">{t}</div>
              <p className="mt-3 text-[12.5px] leading-[1.75] text-white/65">{d}</p>
              <div className="mt-5 h-px bg-gradient-to-r from-[#9DB4FF]/60 to-transparent" />
            </Card>
          ))}
        </div>
        <p className="mt-8 text-[12px] text-white/45 max-w-2xl">
          Forealco does not present these areas as generic sector promises. Each opportunity is
          assessed through structure, feasibility, local fit, partner readiness, institutional
          context, and execution logic.
        </p>
      </section>

      {/* NEXT STEP */}
      <section
        data-testid="markets-next-step"
        className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-28"
      >
        <div className="border border-white/10 bg-white/[0.025] p-10 md:p-14 relative overflow-hidden">
          <div
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, #9DB4FF 50%, transparent)",
              opacity: 0.5,
            }}
            aria-hidden="true"
          />
          <MonoLabel>NEXT STEP</MonoLabel>
          <h2 className="h-display mt-5 text-[30px] md:text-[42px] leading-[1.12] max-w-4xl">
            Define the market route <Em>before moving forward.</Em>
          </h2>
          <p className="mt-6 max-w-2xl text-[15px] leading-[1.9] text-white/70">
            A market can look attractive before the route is clear. Forealco helps frame the
            opportunity, test the market logic, identify the required support layers, and define
            the next practical move.
          </p>
          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <Link
              to="/explore-opportunities"
              data-testid="markets-next-cta-primary"
              className="btn-primary"
            >
              Explore Opportunities →
            </Link>
            <Link
              to="/solutions"
              data-testid="markets-next-cta-secondary"
              className="btn-secondary"
            >
              See Solutions
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

/* ───────────────── Connected Route ───────────────── */
function ConnectedRoute() {
  const lanes = [
    {
      tag: "Türkiye",
      body: "Türkiye can provide manufacturing strength, technology suppliers, service providers, industrial know-how, defense-related capability, and advisory relationships.",
    },
    {
      tag: "Kosovo",
      body: "Kosovo can provide a developing operating base, local coordination environment, regional access point, digital and institutional modernization landscape, and practical entry point into the Western Balkans.",
    },
    {
      tag: "United Kingdom",
      body: "The United Kingdom can provide company formation routes, international credibility, investor-facing structure, commercial visibility, and wider business network access.",
    },
  ];

  return (
    <section
      data-testid="markets-connected-route"
      className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-32"
    >
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-5">
          <MonoLabel>CONNECTED MARKET ROUTES</MonoLabel>
          <h2 className="h-display mt-5 text-[34px] md:text-[44px] leading-[1.08]">
            Kosovo, Türkiye and the UK as a <Em>practical route logic.</Em>
          </h2>
          <div className="mt-7 max-w-md space-y-5 text-[14px] leading-[1.9] text-white/70">
            <p>
              Forealco's market view is route-based. Kosovo, Türkiye, and the United Kingdom are
              not treated only as separate jurisdictions. For selected companies, they can form a
              connected business route involving production, technology, advisory support, company
              structuring, service commercialization, startup visibility, institutional access, and
              international contracting.
            </p>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 space-y-3">
          {lanes.map(({ tag, body }, i) => (
            <Card
              key={tag}
              className="p-6 relative overflow-hidden"
              data-testid={`route-lane-${i}`}
            >
              <div className="flex items-start gap-5">
                <div className="shrink-0 w-12 text-right">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-[#9DB4FF]">
                    0{i + 1}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="font-display text-[22px] leading-[1.2]">{tag}</div>
                  <p className="mt-3 text-[13px] leading-[1.85] text-white/70">{body}</p>
                </div>
              </div>
            </Card>
          ))}

          <div className="border-t border-white/10 pt-6 mt-2">
            <p className="text-[14px] leading-[1.9] text-white/65 max-w-2xl">
              The value is not in naming the countries. The value is in designing the route: which
              country serves which function, which entity does what, how contracts and invoices
              move, how banking and accounting are managed, and how the business becomes
              operational rather than only registered.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
